import { useEffect, useState } from 'react'
import { useI18n } from '../i18n'

/**
 * Polls /version.json (served no-cache) and compares the deployed build id to
 * the one baked into this running bundle. When they differ, a newer build has
 * been deployed while this tab stayed open — so we surface a gentle "reload"
 * banner. This is what stops a stale tab from silently running an old (possibly
 * buggy) bundle after a deploy.
 */
export function UpdatePrompt() {
  const { t } = useI18n()
  const [stale, setStale] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (stale) return // stop polling once we know an update is waiting
    let active = true
    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
        if (!res.ok) return
        const data = (await res.json()) as { build?: string }
        if (active && data.build && data.build !== __BUILD_ID__) setStale(true)
      } catch {
        // offline or transient — try again next tick
      }
    }
    const id = window.setInterval(check, 4 * 60_000)
    const onFocus = () => check() // returning to the tab is the common moment
    window.addEventListener('focus', onFocus)
    return () => {
      active = false
      window.clearInterval(id)
      window.removeEventListener('focus', onFocus)
    }
  }, [stale])

  if (!stale || dismissed) return null

  return (
    <div className="fixed inset-x-0 top-0 z-[120] flex justify-center px-4 pt-3">
      <div className="flex items-center gap-3 rounded-2xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-800 px-4 py-2.5 text-sm shadow-lift animate-fade-up">
        <span className="font-medium">✨ {t('update.available')}</span>
        <button onClick={() => window.location.reload()} className="btn-primary !px-4 !py-1.5 text-xs">
          {t('update.reload')}
        </button>
        <button onClick={() => setDismissed(true)} aria-label={t('update.later')} className="grid h-6 w-6 place-items-center rounded-full text-brand-400 hover:bg-brand-100 hover:text-brand-600 dark:hover:bg-brand-700">
          ✕
        </button>
      </div>
    </div>
  )
}
